import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  LuUserPlus,
  LuShare2,
  LuWallet,
  LuFolderOpen,
  LuLayoutDashboard,
  LuLink,
  LuHeadphones,
} from 'react-icons/lu';

/* ──────────────────────────── helpers ──────────────────────────── */
const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

/* ──────────────────────────── DATA ──────────────────────────── */
const steps = [
  {
    num: '01',
    title: 'REGISTRATI',
    desc: 'Compila il form in 2 minuti e ricevi il tuo link affiliato personale.',
    icon: <LuUserPlus size={24} />,
  },
  {
    num: '02',
    title: 'SEGNALA',
    desc: 'Condividi il tuo link e presenta i nostri servizi a potenziali clienti.',
    icon: <LuShare2 size={24} />,
  },
  {
    num: '03',
    title: 'GUADAGNA',
    desc: 'Ricevi pagamenti mensili puntuali per ogni contratto chiuso.',
    icon: <LuWallet size={24} />,
  },
];

const referrable = [
  'Siti web e gestione',
  'Marketing e ADS',
  'AI e automazioni',
  'Legale e fiscale',
  'Gare e appalti',
];

const tools = [
  {
    icon: <LuFolderOpen size={22} />,
    title: 'Marketing Kit',
    desc: 'Materiale grafico, pitch deck e template pronti all’uso per presentare i nostri servizi.',
  },
  {
    icon: <LuLayoutDashboard size={22} />,
    title: 'Dashboard Personale',
    desc: 'Monitora lead, conversioni e commissioni in tempo reale dal tuo pannello dedicato.',
  },
  {
    icon: <LuLink size={22} />,
    title: 'Link Tracciati',
    desc: 'Cookie attivo per 90 giorni: ogni referral ti viene attribuito anche se il cliente converte dopo settimane.',
  },
  {
    icon: <LuHeadphones size={22} />,
    title: 'Supporto Dedicato',
    desc: 'Un partner manager a tua disposizione per strategie, domande e assistenza continua.',
  },
];

/* ════════════════════════════════════════════════════════════════
   COMPONENT
   ════════════════════════════════════════════════════════════════ */
const AffiliatePage = () => {
  /* ── state ── */
  const [isScrolled, setIsScrolled] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  /* ── SEO: title ── */
  useEffect(() => {
    document.title = 'Programma Affiliazione | Tre Lune Technologies';
  }, []);

  /* ── scroll listener for navbar ── */
  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* ── scroll helper ── */
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  /* ── form handlers ── */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      if (
        !formData.firstName.trim() ||
        !formData.lastName.trim() ||
        !formData.email.trim()
      ) {
        throw new Error('I campi Nome, Cognome ed Email sono obbligatori');
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        throw new Error('Formato email non valido');
      }

      const fd = new FormData();
      fd.append('first-name', formData.firstName);
      fd.append('last-name', formData.lastName);
      fd.append('email', formData.email);
      fd.append('phone', formData.phone);
      fd.append('subject', 'Nuova Richiesta Affiliazione');
      fd.append('source', 'affiliazione');
      fd.append(
        'message',
        `Telefono: ${formData.phone || 'N/D'}\n\nNote: ${formData.message || 'Nessuna'}`
      );

      const res = await fetch('/send-mail.php', {
        method: 'POST',
        body: fd,
      });

      const raw = await res.text();
      let data = {};
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch {
        throw new Error('Invio fallito');
      }
      if (!res.ok || data.status === 'error') throw new Error(data.message || 'Invio fallito');

      setSubmitStatus('success');
      setFormData({ firstName: '', lastName: '', email: '', phone: '', message: '' });
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ════════════════════════════════════════════════
     RENDER
     ════════════════════════════════════════════════ */
  return (
    <div className="affiliate-page">
      {/* BG effects (same as main site) */}
      <div className="bg-glow bg-glow-1"></div>
      <div className="bg-glow bg-glow-2"></div>

      {/* ─── 1. NAVBAR ─── */}
      <nav className={`navbar aff-navbar ${isScrolled ? 'scrolled' : ''}`}>
        <div className="container nav-container">
          <a href="/" className="nav-logo">
            <img src="/media/3LT_Logo.png" alt="Tre Lune" height="32" />
            <span>Tre Lune</span>
          </a>
          <a href="/" className="btn btn-outline" style={{ padding: '0.5rem 1.2rem', fontSize: '0.8rem' }}>
            Torna al sito
          </a>
        </div>
      </nav>

      {/* ─── 2. HERO ─── */}
      <section className="hero-section aff-hero">
        <div className="container">
          <div className="hero-content">
            <motion.div
              className="hero-badges"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="hero-badge">01. Programma Partner</span>
              <span className="hero-badge">02. Commissioni</span>
            </motion.div>

            <motion.h1
              className="hero-title"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              DIVENTA PARTNER.
              <br />
              GUADAGNA CON NOI.
            </motion.h1>

            <motion.p
              className="hero-subtitle"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
            >
              Presenti un cliente, chiudiamo noi. Commissione dal 10% al 15% sul contratto. Pagamenti mensili, cookie 90 giorni.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6 }}
            >
              <button
                className="btn btn-primary"
                onClick={() => scrollTo('aff-form')}
              >
                Inizia ora
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── 3. COME FUNZIONA ─── */}
      <section className="section">
        <div className="container">
          <motion.h2
            className="text-gradient"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            TRE SEMPLICI PASSI PER GUADAGNARE
          </motion.h2>

          <motion.div
            className="bento-grid"
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {steps.map((s) => (
              <motion.div key={s.num} variants={fadeUp} className="glass-panel aff-step-card">
                <span className="aff-step-num">{s.num}</span>
                <div className="aff-step-icon-box">
                  <span style={{ color: 'var(--accent-primary)' }}>{s.icon}</span>
                </div>
                <h3 className="aff-step-title">{s.title}</h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>{s.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ─── 4. COSA PUOI SEGNALARE ─── */}
      <section className="section">
        <div className="container">
          <motion.h2
            className="text-gradient"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Cosa puoi segnalare
          </motion.h2>
          <motion.p
            style={{ color: 'var(--text-secondary)', maxWidth: 600, marginBottom: '2rem' }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            La commissione si calcola sul contratto chiuso. Listino e kit li ricevi dopo la registrazione.
          </motion.p>

          <motion.div
            className="glass-panel aff-services-table"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            {referrable.map((name) => (
              <div key={name} className="aff-service-row">
                <span className="aff-service-bullet">■</span>
                <span className="aff-service-name">{name}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ─── 5. STRUTTURA COMMISSIONALE ─── */}
      <section className="section">
        <div className="container">
          <motion.h2
            className="text-gradient"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            UNA STRUTTURA PROVVIGIONALE CHE TI PREMIA
          </motion.h2>

          <div className="aff-commission-grid">
            <motion.div
              className="glass-panel aff-commission-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <span className="aff-commission-label">LIVELLO STANDARD</span>
              <span className="aff-commission-pct">10%</span>
              <p style={{ color: 'var(--text-secondary)' }}>
                Su tutti i contratti fino a 30.000€ di fatturato generato.
              </p>
            </motion.div>

            <motion.div
              className="glass-panel aff-commission-card aff-commission-elite"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <span className="aff-commission-label">LIVELLO ELITE</span>
              <span className="aff-commission-pct">15%</span>
              <p style={{ color: 'var(--text-secondary)' }}>
                Scatta automaticamente al superamento dei 30.000€ di fatturato.
              </p>
            </motion.div>
          </div>

          <motion.p
            style={{ color: 'var(--text-secondary)', marginTop: '2rem', maxWidth: '42rem' }}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Esempio: contratto sito da 2.000€ → 200€ al livello standard.
          </motion.p>
        </div>
      </section>

      {/* ─── 6. STRUMENTI E SUPPORTO ─── */}
      <section className="section">
        <div className="container">
          <motion.h2
            className="text-gradient"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            IL TUO KIT PER IL SUCCESSO
          </motion.h2>

          <motion.div
            className="aff-tools-grid"
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {tools.map((t, i) => (
              <motion.div key={i} variants={fadeUp} className="glass-panel aff-tool-card">
                <div className="aff-tool-icon">
                  <span style={{ color: 'var(--accent-primary)' }}>{t.icon}</span>
                </div>
                <h3 style={{ fontSize: '1.15rem', marginBottom: '0.5rem' }}>{t.title}</h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: 0 }}>
                  {t.desc}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ─── 7. CTA + FORM ─── */}
      <section className="section" id="aff-form">
        <div className="container">
          <motion.div
            style={{ textAlign: 'center', maxWidth: 700, margin: '0 auto 3rem' }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-gradient" style={{ marginBottom: '1rem' }}>
              PRONTO A SCALARE IL TUO BUSINESS?
            </h2>
            <p style={{ color: 'var(--text-secondary)' }}>
              Unisciti oggi al nostro network di partner. La registrazione è gratuita e
              richiede solo 2 minuti.
            </p>
          </motion.div>

          <motion.form
            onSubmit={handleSubmit}
            className="glass-panel aff-form-panel"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15 }}
          >
            {submitStatus === 'success' && (
              <div className="aff-alert aff-alert-success">
                Registrazione inviata con successo! Ti contatteremo a breve.
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="aff-alert aff-alert-error">
                Si è verificato un errore. Riprova più tardi o scrivici direttamente.
              </div>
            )}

            <div className="aff-form-row">
              <div className="form-group">
                <label className="form-label">Nome *</label>
                <input
                  type="text"
                  name="firstName"
                  className="form-input"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Cognome *</label>
                <input
                  type="text"
                  name="lastName"
                  className="form-input"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="aff-form-row">
              <div className="form-group">
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  name="email"
                  className="form-input"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Telefono</label>
                <input
                  type="tel"
                  name="phone"
                  className="form-input"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Messaggio / Note</label>
              <textarea
                name="message"
                className="form-textarea"
                rows="4"
                value={formData.message}
                onChange={handleChange}
              ></textarea>
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              style={{ width: '100%' }}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Invio in corso...' : 'REGISTRATI ORA'}
            </button>
          </motion.form>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="aff-footer">
        <div className="container">
          <div className="aff-footer-inner">
            <p style={{ margin: 0 }}>
              © 2026 Tre Lune Technologies. P.IVA 08740380723.
            </p>
            <div className="aff-footer-links">
              <a href="/privacy-policy">Informativa privacy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AffiliatePage;
